/**
 * The two forms a person's name takes, and which one may be STORED (#1507).
 *
 * ⚠️ WHY THIS EXISTS. `requestor_name` was written from `user.name`, which
 * arrives in two different shapes depending on how the environment
 * authenticated the caller:
 *
 *   - Okta                        → "Last, First"  ("Requestor, Test")
 *   - the dev auth bypass         → "First Last"   ("Test Requestor")
 *     (`server/config/devAuthBypass.ts` joins `first_name` + `last_name`)
 *
 * So the same person filing a request stored a different spelling in each
 * environment, and any later save that re-asserted the other one was recorded
 * by version history as a real edit and mailed out by the digest as a change
 * nobody had made.
 *
 * ⚠️ THE STORED FORM IS "Last, First", AND THAT IS NOT A PREFERENCE.
 * `formatModifiedByForDisplay` (`server/utils/modifiedByDisplay.ts`, #946)
 * already canonicalises names to "Last, First", and `toClientFormat` returns
 * ITS result in `requestorName` whenever the requestor is also the last
 * modifier — in the same field the edit form writes back. Anything else stored
 * there is therefore overwritten by the server's own display form on the next
 * save. The legacy rows agree: every comma-carrying `requestor_name` in the
 * database is "Last, First".
 *
 * ⚠️ NEVER STORE THE OUTPUT OF `toDisplayPersonName`. That is the whole bug in
 * one sentence: a display form reaching a write path is what produced the
 * phantom deltas. Display at the edge, store the canonical form.
 */

/** A name with its surrounding and inner whitespace collapsed. */
const tidy = (name: string): string => name.trim().replace(/\s*,\s*/u, ', ');

/**
 * A person's name as the database stores it: "Last, First".
 *
 * Idempotent, so applying it to an already-stored value is a no-op — which is
 * what lets it sit on a write path that may receive either form.
 */
export function toStoredPersonName(name: string | null | undefined): string {
  if (!name) return '';
  const trimmed = tidy(name);
  if (trimmed === '') return '';
  // Already "Last, First" — the form we want. Also the only safe reading of a
  // value carrying more than one comma, which this rule does not describe.
  if (trimmed.includes(',')) return trimmed;

  // "First Last" → "Last, First". Only a plain two-part name is rearranged;
  // a single token has no surname to move, and three or more parts cannot be
  // split into given and family names without guessing.
  const parts = trimmed.split(/\s+/u);
  if (parts.length !== 2) return trimmed;
  return `${parts[1]}, ${parts[0]}`;
}

/**
 * A person's name as a screen shows it: "First Last".
 *
 * ⚠️ FOR RENDERING ONLY. Never write this back to the server — see the header.
 */
export function toDisplayPersonName(name: string | null | undefined): string {
  if (!name) return '';
  const trimmed = tidy(name);
  const comma = trimmed.indexOf(',');
  if (comma === -1) return trimmed;

  const last = trimmed.slice(0, comma).trim();
  const rest = trimmed.slice(comma + 1).trim();
  // A second comma is a shape this rule does not describe.
  if (last === '' || rest === '' || rest.includes(',')) return trimmed;
  return `${rest} ${last}`;
}
